import { QueryCombinerType } from 'module/api/filter/types';
import {
  CustomFieldFilter,
  NativeFieldFilter,
  SimpleSearchFilter,
} from 'module/api/filter/types';
import { logger } from 'module/logger';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { Filter } from 'module/types/filter-field/filter/index';
import { FieldFilter } from 'module/types/filter-field/filter/index';
import { ApiFilter } from 'module/ui/components/molecules/field-filter/simple-search-filter/simple-search-filter';
import { keyBy } from 'lodash';
import { v4 as uuid } from 'uuid';
import type { EntityId } from '@reduxjs/toolkit';

const DEFAULT_FILTER: Filter = {
  type: QueryCombinerType.All,
  criteria: [],
};

export const to_filter = (filter: Filter): SimpleSearchFilter => ({
  nativeFields: filter.criteria
    .filter(FieldFilter.isValid)
    .filter((x) => !x.field.userCreated)
    .map(ApiFilter.toNativeFieldFilterFormat),
  customFields: filter.criteria
    .filter(FieldFilter.isValid)
    .filter((x) => x.field.userCreated)
    .map(ApiFilter.toCustomFieldFilterFormat),
  match: filter.type,
});

export interface ApiFilterTransformer<T> {
  from: (parsed: SimpleSearchFilter) => T;
  to: (value: T) => SimpleSearchFilter;
}

const tryParseNativeFieldFilter = (
  filter: NativeFieldFilter,
  lookup: Record<EntityId, FilterableField>,
): FieldFilter | null => {
  const field = lookup[filter.name];

  try {
    return field ? ApiFilter.parse(filter, field) : null;
  } catch (error) {
    logger.error('failed to parse criteria', { filter, error: error as Error });

    return null;
  }
};

const tryParseCustomFieldFilter = (
  filter: CustomFieldFilter,
  lookup: Record<EntityId, FilterableField>,
): FieldFilter | null => {
  const byIdLookup = keyBy(Object.values(lookup), 'fieldId');
  const field = byIdLookup[filter.fieldId];

  try {
    return field ? ApiFilter.parse(filter, field) : null;
  } catch (error) {
    logger.error('failed to parse criteria', { filter, error: error as Error });

    return null;
  }
};

export const from_filter =
  (lookup: Record<string, FilterableField>) =>
  (query: SimpleSearchFilter): Filter =>
    Object.keys(lookup).length === 0
      ? DEFAULT_FILTER
      : {
          type: query.match ?? QueryCombinerType.All,
          criteria: [
            ...(query.nativeFields ?? [])
              .map((x) => tryParseNativeFieldFilter(x, lookup))
              .filter((x) => x != null)
              .map((x) => ({ ...x, id: uuid() }) as FieldFilter),
            ...(query.customFields ?? [])
              .map((x) => tryParseCustomFieldFilter(x, lookup))
              .filter((x) => x != null)
              .map((x) => ({ ...x, id: uuid() }) as FieldFilter),
          ],
        };

export const getApiFilterTransformer = (
  lookup: Record<string, FilterableField>,
): ApiFilterTransformer<Filter> => ({
  from: (simpleSearchFilter) => from_filter(lookup)(simpleSearchFilter),
  to: to_filter,
});
